/**
 * AuthContext.jsx — Contexto global de autenticação.
 * 
 * Provê o estado do usuário logado, funções de login/logout/MFA,
 * e o hook useAuth() para consumo em qualquer componente.
 * 
 * @module AuthContext
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  apiFetch, setTokens, clearAuth, 
  getStoredUser, setStoredUser, getAccessToken 
} from './apiClient';
// ADR-007: o cache de servidor morre junto com a credencial. Cache que sobrevive
// ao logout e dado de um escritorio aparecendo na sessao de outro.
import { limparCacheDeSessao } from './queryClient';

/** @type {React.Context} */
const AuthContext = createContext(null);

/**
 * Estados possíveis do fluxo de login.
 * @enum {string}
 */
export const LOGIN_STAGES = {
  LOGIN: 'login',
  MFA: 'mfa',
  FIRST_ACCESS: 'primeiro_acesso',
  RECOVERY: 'recuperacao'
};

/**
 * Provider de autenticação — envolve a aplicação inteira.
 * Gerencia: usuário logado, tokens JWT, login, logout, MFA, troca de senha.
 */
export function AuthProvider({ children }) {
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [etapaLogin, setEtapaLogin] = useState(LOGIN_STAGES.LOGIN);
  // Achado 8 (revisão do Codex): resposta atrasada não restaura sessão cancelada.
  const geracaoDaSessao = useRef(0);
  const [erroLogin, setErroLogin] = useState('');
  const [carregando, setCarregando] = useState(false);
  // Desafio do 2º fator (token de vida curta). Só em memória.
  const [desafioMFA, setDesafioMFA] = useState(null);
  // Verdadeiro enquanto verifica sessão do localStorage (evita redirect prematuro)
  const [inicializando, setInicializando] = useState(true);

  // Restaurar sessão do localStorage ao montar
  useEffect(() => {
    const userSalvo = getStoredUser();
    const token = getAccessToken();
    if (userSalvo && token) {
      setUsuarioLogado(userSalvo);
    } else if (userSalvo && !token) {
      clearAuth();
      limparCacheDeSessao();
    }
    // Sinaliza que a inicialização terminou — rotas podem ser renderizadas
    setInicializando(false);
  }, []);

  /**
   * Realiza login com email e senha.
   * @param {string} email 
   * @param {string} senha 
   * @returns {Promise<{success: boolean, firstAccess?: boolean}>}
   */
  const login = useCallback(async (email, senha) => {
    setErroLogin('');
    setCarregando(true);
    const minhaGeracao = ++geracaoDaSessao.current;
    try {
      const res = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, senha })
      });
      const dados = await res.json();
      if (minhaGeracao !== geracaoDaSessao.current) {
        return { success: false, cancelado: true };
      }

      if (res.ok) {
        if (dados.status === 'mfa_required') {
          setDesafioMFA(dados.challenge_token);
          setEtapaLogin(LOGIN_STAGES.MFA);
          return { success: true, mfaRequired: true };
        }

        if (dados.access_token) {
          setTokens(dados.access_token, dados.refresh_token);
        }

        if (dados.first_access === 1) {
          setUsuarioLogado(dados.usuario);
          setStoredUser(dados.usuario);
          setEtapaLogin(LOGIN_STAGES.FIRST_ACCESS);
          return { success: true, firstAccess: true };
        }

        setUsuarioLogado(dados.usuario);
        setStoredUser(dados.usuario);
        setEtapaLogin(LOGIN_STAGES.LOGIN);
        return { success: true };
      } else {
        // Suporta formato customizado { error: { message } } e o padrão { detail }
        const msgErro =
          (dados?.detail && typeof dados.detail === 'string' ? dados.detail : null) ||
          dados?.error?.message ||
          'Erro no login. Verifique suas credenciais.';
        setErroLogin(msgErro);
        return { success: false, error: msgErro };
      }
    } catch (err) {
      setErroLogin('Servidor indisponível.');
      return { success: false, error: 'Servidor indisponível.' };
    } finally {
      setCarregando(false);
    }
  }, []);

  /**
   * Conclui o 2º fator do login. O desafio (token) fica só em memória: nunca
   * vai ao localStorage. `codigo` é o de 6 dígitos ou um código reserva.
   */
  const verifyMFA = useCallback(async (codigo) => {
    if (!desafioMFA) {
      setErroLogin('A verificação expirou. Entre de novo.');
      return { success: false };
    }
    setErroLogin('');
    setCarregando(true);
    const minhaGeracao = geracaoDaSessao.current;
    try {
      const res = await apiFetch('/api/auth/verify-mfa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${desafioMFA}` },
        body: JSON.stringify({ codigo })
      });
      const dados = await res.json();
      if (minhaGeracao !== geracaoDaSessao.current) {
        return { success: false, cancelado: true };
      }
      if (res.ok) {
        setDesafioMFA(null);
        if (dados.access_token) {
          setTokens(dados.access_token, dados.refresh_token);
        }
        setUsuarioLogado(dados.usuario);
        setStoredUser(dados.usuario);
        setEtapaLogin(LOGIN_STAGES.LOGIN);
        return { success: true };
      } else {
        const msgErro =
          (dados?.detail && typeof dados.detail === 'string' ? dados.detail : null) ||
          dados?.error?.message ||
          'Código inválido.';
        // Desafio esgotado ou vencido: sem ele não há como tentar de novo.
        if (res.status === 401 && /expirou|Faça o login|Muitas tentativas/i.test(msgErro)) {
          setDesafioMFA(null);
        }
        setErroLogin(msgErro);
        return { success: false };
      }
    } catch (err) {
      setErroLogin('Erro na verificação do 2FA.');
      return { success: false };
    } finally {
      setCarregando(false);
    }
  }, [desafioMFA]);

  /** Volta ao login e descarta o desafio em memória. */
  const cancelarMFA = useCallback(() => {
    // Resposta de verificação ainda em voo não pode gravar tokens depois do cancelamento.
    geracaoDaSessao.current += 1;
    setDesafioMFA(null);
    setErroLogin('');
    setEtapaLogin(LOGIN_STAGES.LOGIN);
  }, []);

  /**
   * Troca de senha obrigatória (primeiro acesso).
   * @param {string} senhaAtual 
   * @param {string} novaSenha 
   */
  const changePassword = useCallback(async (userId, senhaAtual, novaSenha) => {
    setCarregando(true);
    try {
      const res = await apiFetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_usuario: userId, senha_atual: senhaAtual, nova_senha: novaSenha })
      });
      if (res.ok) {
        setEtapaLogin(LOGIN_STAGES.LOGIN);
        return { success: true };
      } else {
        const d = await res.json();
        return { success: false, error: d.detail || 'Erro ao trocar senha' };
      }
    } catch (e) {
      return { success: false, error: 'Erro de servidor' };
    } finally {
      setCarregando(false);
    }
  }, []);

  /**
   * Logout — limpa tokens e estado.
   */
  const logout = useCallback(() => {
    geracaoDaSessao.current += 1;
    setUsuarioLogado(null);
    clearAuth();
    limparCacheDeSessao();
    setEtapaLogin(LOGIN_STAGES.LOGIN);
    setErroLogin('');
  }, []);

  /**
   * Atualiza dados do usuário logado (ex: foto, MFA status).
   * @param {Object} updates - Campos a atualizar
   */
  const updateUser = useCallback((updates) => {
    setUsuarioLogado(prev => {
      const updated = { ...prev, ...updates };
      setStoredUser(updated);
      return updated;
    });
  }, []);

  /**
   * Verifica se o usuário tem permissão para acessar um módulo.
   * @param {string} modulo - Nome do módulo (ex: 'crm', 'financeiro')
   * @returns {boolean}
   */
  const temPermissao = useCallback((modulo) => {
    if (!usuarioLogado) return false;
    if (usuarioLogado.is_master) return true;
    if (modulo === 'home' || modulo === 'configuracoes') return true;

    const perms = usuarioLogado.permissions;
    // Trata {} (objeto vazio) como null — fallback para lógica baseada em perfil.
    // Isso acontece quando nenhum setor/cargo/usuário tem permissões configuradas.
    const hasPerms = perms && typeof perms === 'object' && Object.keys(perms).length > 0;

    if (!hasPerms) {
      const p = usuarioLogado.perfil;
      if (['Admin', 'T.I.', 'Administrador', 'Master', 'Diretor / Sócio'].includes(p)) return true;
      if (p === 'Advogado') return ['home', 'dashboard', 'crm', 'juridico', 'documentos', 'radar', 'jurisprudencia'].includes(modulo);
      if (p === 'Financeiro') return ['home', 'dashboard', 'crm', 'financeiro'].includes(modulo);
      return false;
    }

    if (perms.admin === true) return true;
    if (modulo === 'dashboard') return !!perms.dashboard;
    if (modulo === 'crm') return !!perms.crm?.read;
    if (modulo === 'financeiro') return !!perms.financeiro?.read;

    if (['juridico', 'documentos', 'radar', 'jurisprudencia', 'contratos', 'agenda', 'kanban', 'propostas'].includes(modulo)) {
      return !!perms.operacao?.read;
    }

    return false;
  }, [usuarioLogado]);

  /**
   * Checagem RBAC granular fiel ao backend (permission_resolver.check_permission):
   * bypass is_master / perfil admin nativo, e os formatos wildcard "acesso total"
   * (admin=true, admin.<acao>=true, super-chave '*'). Diferente de temPermissao,
   * NÃO usa o fallback permissivo por nome de perfil — assim uma aba só aparece
   * quando o backend de fato concederia (evita mostrar aba que daria 403).
   * @param {string} modulo - Domínio (ex: 'equipe', 'acessos', 'configuracoes').
   * @param {'read'|'write'|'delete'} [acao='read']
   * @returns {boolean}
   */
  const checarPermissao = useCallback((modulo, acao = 'read') => {
    if (!usuarioLogado) return false;
    if (usuarioLogado.is_master) return true;
    if (['admin', 'Administrador'].includes(usuarioLogado.perfil)) return true;
    const perms = usuarioLogado.permissions;
    if (!perms || typeof perms !== 'object') return false;
    for (const wk of ['admin', '*']) {
      const wv = perms[wk];
      if (wv === true) return true;
      if (wv && typeof wv === 'object' && wv[acao] === true) return true;
    }
    const mp = perms[modulo];
    if (mp == null) return false;
    if (typeof mp === 'boolean') return mp;
    if (typeof mp === 'object') return !!mp[acao];
    return false;
  }, [usuarioLogado]);

  const value = {
    usuarioLogado,
    setUsuarioLogado,
    etapaLogin,
    setEtapaLogin,
    erroLogin,
    setErroLogin,
    carregando,
    inicializando,
    login,
    verifyMFA,
    cancelarMFA,
    changePassword,
    logout,
    updateUser,
    temPermissao,
    checarPermissao,
    isAuthenticated: !!usuarioLogado
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook para consumir o contexto de autenticação.
 * @returns {Object} - Estado e funções de auth
 * @example
 * const { usuarioLogado, login, logout, temPermissao } = useAuth();
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth() deve ser usado dentro de um <AuthProvider>');
  }
  return context;
}

export default AuthContext;
